/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useContext } from 'react';

import { ActionMenu, ActionMenuItem } from 'bundles/grading/components/common/ActionMenu';
import VisibilityTag from 'bundles/grading/components/common/VisibilityTag';
import { SGAPageContext } from 'bundles/grading/components/staff-graded-assignments/SGAPageContext';
import { generateUpdateGradeVisibilityMutationVariables } from 'bundles/grading/utils/apiUtils';
import { isGradeHiddenInGradebookEntry } from 'bundles/grading/utils/gradeUtils';

import _t from 'i18n!nls/grading';

const styles = {
  menu: css`
    top: calc(var(--cds-spacing-100) * -1) !important;
  `,
};

// Component for the assignment grade visibility controls in right panel
const GradeVisibility = () => {
  const { mutations, pageData, submissionData, isPrivateSession } = useContext(SGAPageContext);

  if (!mutations || !isPrivateSession) {
    return null;
  }

  const gradebookEntry = submissionData.data?.gradebookEntry || null;

  const updateGradeVisibility = async (isHidden: boolean) => {
    // ensure selected hidden status isn't the current status
    if (isHidden !== isGradeHiddenInGradebookEntry(gradebookEntry)) {
      await mutations?.updateGradeVisibility({
        variables: generateUpdateGradeVisibilityMutationVariables(
          isHidden,
          submissionData.data?.groupId || '',
          pageData.currentItemId,
          submissionData.data?.submitter?.id || '',
          pageData.gradingGroupId
        ),
      });
    }
  };

  return (
    <span aria-label={_t('Grade Visibility')}>
      <ActionMenu
        buttonLabel={
          <VisibilityTag
            isHidden={isGradeHiddenInGradebookEntry(gradebookEntry)}
            disabled={mutations?.updateGradeVisibilityStatus.loading}
            showTooltip
          />
        }
        id="grade-visibility-dropdown"
        iconPosition="before"
        buttonSize="small"
        buttonDisabled={mutations?.updateGradeVisibilityStatus.loading}
        originHorizontal="right"
        cssOverride={styles.menu}
        fitContentWidth
        inheritStyle
        inlineContainer
      >
        <ActionMenuItem
          label={_t('Show grades to learner')}
          onClick={() => updateGradeVisibility(false)}
          selected={!isGradeHiddenInGradebookEntry(gradebookEntry)}
          data-testid="grade-visibility-action-show"
        />
        <ActionMenuItem
          label={_t('Hide grades from learner')}
          onClick={() => updateGradeVisibility(true)}
          selected={isGradeHiddenInGradebookEntry(gradebookEntry)}
          data-testid="grade-visibility-action-hide"
        />
      </ActionMenu>
    </span>
  );
};

export default GradeVisibility;
